import { useState, useEffect, useCallback } from 'react';
import { locales, Locale, LocaleKeys } from '../locales';
import { secureLogger } from '../utils/secureLogger';
import { API_CONFIG } from '../config/api';

export const useLocale = () => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem('locale');
    secureLogger.debug('Locale initialization', { hasSaved: !!saved });
    const result = (saved && saved !== 'undefined' && saved !== 'null') ? saved as Locale : 'ja';
    secureLogger.debug('Final initial locale set');
    return result;
  });
  const [user, setUser] = useState<{ user: { id: string } } | null>(null);
  const [isLocaleLoaded, setIsLocaleLoaded] = useState(false);
  
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
  };

  // ローカルストレージに保存（ユーザーが手動で変更した場合のみ）
  useEffect(() => {
    if (isLocaleLoaded) {
      secureLogger.debug('Locale changed, saving to localStorage');
      localStorage.setItem('locale', locale);
    }
  }, [locale, isLocaleLoaded]);

  // ユーザーの言語設定をDBから取得
  const loadUserLocale = useCallback(async (userData: { user: { id: string; locale?: string } }) => {
    try {
      secureLogger.debug('Loading user locale');
      
      // バックエンドのレスポンスに既にlocaleが含まれている場合はそれを使用
      if (userData?.user?.locale) {
        secureLogger.debug('Using locale from userData');
        setLocaleState(userData.user.locale as Locale);
        // DBの値でローカルストレージを更新
        localStorage.setItem('locale', userData.user.locale);
        setIsLocaleLoaded(true);
        return;
      }
      
      // フォールバック: APIから取得
      if (userData?.user?.id) {
        secureLogger.debug('Fetching locale from API');
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        
        if (!token) {
          secureLogger.warn('No token available, using localStorage fallback');
          const saved = localStorage.getItem('locale');
          setLocaleState((saved && saved !== 'undefined') ? saved as Locale : 'ja');
          setIsLocaleLoaded(true);
          return;
        }
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/user/locale`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          secureLogger.debug('API returned locale');
          setLocaleState(data.locale);
          localStorage.setItem('locale', data.locale);
          setIsLocaleLoaded(true);
        } else {
          secureLogger.warn('API failed, using localStorage fallback');
          const saved = localStorage.getItem('locale');
          if (saved && saved !== 'undefined') {
            setLocaleState(saved as Locale);
          } else {
            setLocaleState('ja');
          }
          setIsLocaleLoaded(true);
        }
      } else {
        secureLogger.debug('No user ID, marking as loaded');
        setIsLocaleLoaded(true);
      }
    } catch (error) {
      secureLogger.error('Locale load error', { error: 'Failed to load user locale' });
      // エラー時はローカルストレージから復元
      const saved = localStorage.getItem('locale');
      if (saved && saved !== 'undefined') {
        setLocaleState(saved as Locale);
      } else {
        setLocaleState('ja');
      }
      setIsLocaleLoaded(true);
    }
  }, []);

  // 言語設定をDBに保存
  const saveUserLocale = async (newLocale: Locale) => {
    try {
      secureLogger.debug('Attempting to save locale');
      if (user?.user?.id) {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        
        if (!token) {
          secureLogger.warn('No token available for locale save');
          return;
        }
        
        const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/user/locale`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            locale: newLocale
          })
        });
        secureLogger.debug('Locale save response', { status: response.status });
        if (response.ok) {
          await response.json();
          secureLogger.info('Locale save success');
        } else {
          secureLogger.error('Locale save failed');
        }
      } else {
        secureLogger.warn('No user ID available for locale save');
      }
    } catch (error) {
      secureLogger.error('Locale save error', { error: 'Failed to save user locale' });
    }
  };

  // 言語設定変更時の処理
  const updateLocale = async (newLocale: Locale) => {
    secureLogger.debug('Changing locale');
    setLocaleState(newLocale);
    localStorage.setItem('locale', newLocale);
    secureLogger.debug('Saved to localStorage');
    await saveUserLocale(newLocale);
  };

  const t = (key: LocaleKeys): string => {
    const translation = locales[locale][key];
    if (!translation) {
      return key;
    }
    return translation;
  };

  return { 
    locale, 
    setLocale: updateLocale, 
    t, 
    setUser, 
    loadUserLocale,
    isLocaleLoaded,
    setIsLocaleLoaded
  };
};
